/**
 * CommitmentCard - reusable card summarizing a SyndicationCommitment.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, FileCheck } from 'lucide-react';
import {
  SyndicationCommitment,
  CoInvestVisibility,
} from '../types/syndication';
import WireStatusStepper from './WireStatusStepper';

interface CommitmentCardProps {
  commitment: SyndicationCommitment;
  visibility?: CoInvestVisibility;
  anonymousIndex?: number;
  showRoundLink?: boolean;
  roundName?: string;
}

const formatCurrency = (n?: number | null): string => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const typeBadge = (t: SyndicationCommitment['commitment_type']): string =>
  t === 'hard'
    ? 'bg-green-100 text-green-700 border-green-300'
    : 'bg-amber-100 text-amber-700 border-amber-300';

export const formatInvestorName = (
  commitment: SyndicationCommitment,
  visibility: CoInvestVisibility = 'transparent',
  anonymousIndex?: number
): string => {
  const realName =
    commitment.investor?.full_name || `Investor #${commitment.investor_id}`;
  if (visibility === 'anonymized') {
    return anonymousIndex != null
      ? `Investor #${anonymousIndex}`
      : 'Investor (anonymized)';
  }
  if (visibility === 'lead_only' && !commitment.lead_investor) {
    return 'Investor (confidential)';
  }
  return realName;
};

const CommitmentCard: React.FC<CommitmentCardProps> = ({
  commitment,
  visibility = 'transparent',
  anonymousIndex,
  showRoundLink,
  roundName,
}) => {
  const name = formatInvestorName(commitment, visibility, anonymousIndex);
  const firmName =
    visibility === 'transparent' ||
    (visibility === 'lead_only' && commitment.lead_investor)
      ? commitment.investor?.firm_name
      : null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-sm font-semibold text-gray-900 truncate">
              {name}
            </div>
            {commitment.lead_investor && (
              <span
                className="inline-flex items-center text-amber-600"
                title="Lead investor"
              >
                <Crown className="w-3.5 h-3.5" />
              </span>
            )}
          </div>
          {firmName && (
            <div className="text-xs text-gray-500 truncate">{firmName}</div>
          )}
          {showRoundLink && (
            <div className="text-xs text-gray-500 mt-1">
              <Link
                to={`/syndication/${commitment.round_id}`}
                className="text-blue-600 hover:underline"
              >
                {roundName || `Round #${commitment.round_id}`}
              </Link>
            </div>
          )}
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-sm font-semibold text-gray-900">
            {formatCurrency(commitment.amount)}
          </div>
          <span
            className={`inline-block mt-1 px-2 py-0.5 rounded text-[10px] font-medium capitalize border ${typeBadge(
              commitment.commitment_type
            )}`}
          >
            {commitment.commitment_type}
          </span>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <WireStatusStepper status={commitment.wire_status} compact />
        {commitment.subscription_doc_signed && (
          <span className="inline-flex items-center text-xs text-green-700">
            <FileCheck className="w-3.5 h-3.5 mr-1" />
            Signed
          </span>
        )}
      </div>

      {commitment.allocated_amount != null && commitment.allocated_amount > 0 && (
        <div className="mt-2 text-xs text-gray-600">
          Allocated:{' '}
          <span className="font-medium text-gray-900">
            {formatCurrency(commitment.allocated_amount)}
          </span>
        </div>
      )}
    </div>
  );
};

export default CommitmentCard;
